
import { useState, useMemo, useEffect } from 'react';
import { subDays } from 'date-fns';

export type DateField = 'requested_date' | 'created_at' | 'scheduled_date';

export interface FilterState {
  dateRange: { start: Date; end: Date }; // Sempre tem um valor padrão (últimos 15 dias)
  dateField: DateField | null;
  materials: string[];
  states: string[];
  establishments: string[];
  generators: string[];
  statuses: string[];
  packagings: string[];
  residues: string[];
  /** Filtro "tem MTR": true = só com MTR, false = só sem MTR, null = todos. */
  hasMtr: boolean | null;
}

// Default date range: last 15 days - FORA do hook para evitar re-criação
const getDefaultDateRange = () => ({
  start: subDays(new Date(), 15),
  end: new Date()
});

// Chave para armazenar os filtros no sessionStorage
const FILTERS_STORAGE_KEY = 'onDemandCollects_filters';

// Função para carregar os filtros do sessionStorage
const loadFiltersFromStorage = (defaultDateRange: { start: Date; end: Date }): FilterState => {
  try {
    const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Converter as datas de string para Date e garantir que todos os campos existam
      return {
        dateRange: parsed.dateRange ? {
          start: new Date(parsed.dateRange.start),
          end: new Date(parsed.dateRange.end)
        } : defaultDateRange,
        dateField: parsed.dateField ?? null,
        materials: parsed.materials ?? [],
        states: parsed.states ?? [],
        establishments: parsed.establishments ?? [],
        generators: parsed.generators ?? [],
        statuses: parsed.statuses ?? [],
        packagings: parsed.packagings ?? [],
        residues: parsed.residues ?? [],
        hasMtr: parsed.hasMtr ?? null
      };
    }
  } catch (error) {
    console.error('Erro ao carregar filtros do sessionStorage:', error);
  }
  
  // Retorna os valores padrão se não houver nada armazenado ou houver erro
  return {
    dateRange: defaultDateRange,
    dateField: null,
    materials: [],
    states: [],
    establishments: [],
    generators: [],
    statuses: [],
    packagings: [],
    residues: [],
    hasMtr: null
  };
};

// Função para salvar os filtros no sessionStorage
const saveFiltersToStorage = (filters: FilterState) => {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Erro ao salvar filtros no sessionStorage:', error);
  }
};

export const useFilters = () => {
  // Memoizar o range padrão para evitar re-renders desnecessários
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);

  // Inicializar com os filtros do sessionStorage (se existirem)
  const [filters, setFilters] = useState<FilterState>(() => 
    loadFiltersFromStorage(defaultDateRange)
  );

  // Sempre que os filtros mudarem, salvar no sessionStorage
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  const updateDateRange = (dateRange: { start: Date; end: Date }) => {
    console.log('🔄 useFilters: Updating date range:', dateRange);
    setFilters(prev => ({ ...prev, dateRange }));
  };

  const updateDateField = (dateField: DateField | null) => {
    setFilters(prev => ({ 
      ...prev, 
      dateField,
      // Reset dateRange when dateField changes
      dateRange: dateField ? (prev.dateRange || defaultDateRange) : null
    }));
  };

  const updateMaterials = (materials: string[]) => {
    setFilters(prev => ({ ...prev, materials }));
  };

  const updateStates = (states: string[]) => {
    setFilters(prev => ({ ...prev, states }));
  };

  const updateEstablishments = (establishments: string[]) => {
    setFilters(prev => ({ ...prev, establishments }));
  };

  const updateGenerators = (generators: string[]) => {
    setFilters(prev => ({ ...prev, generators }));
  };

  const updateStatuses = (statuses: string[]) => {
    setFilters(prev => ({ ...prev, statuses }));
  };

  const updatePackagings = (packagings: string[]) => {
    setFilters(prev => ({ ...prev, packagings }));
  };

  const updateResidues = (residues: string[]) => {
    setFilters(prev => ({ ...prev, residues }));
  };

  const updateHasMtr = (hasMtr: boolean | null) => {
    setFilters(prev => ({ ...prev, hasMtr }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: defaultDateRange, // Volta para os últimos 15 dias ao invés de null
      dateField: null,
      materials: [],
      states: [],
      establishments: [],
      generators: [],
      statuses: [],
      packagings: [],
      residues: [],
      hasMtr: null
    });
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.dateField !== null ||
           filters.materials.length > 0 || 
           filters.states.length > 0 || 
           filters.establishments.length > 0 ||
           filters.generators.length > 0 ||
           filters.statuses.length > 0 ||
           filters.packagings.length > 0 ||
           filters.residues.length > 0 ||
           filters.hasMtr !== null;
  }, [filters]);

  return {
    filters,
    updateDateRange,
    updateDateField,
    updateMaterials,
    updateStates,
    updateEstablishments,
    updateGenerators,
    updateStatuses,
    updatePackagings,
    updateResidues,
    updateHasMtr,
    clearFilters,
    hasActiveFilters
  };
};
